import React from 'react'
import PropTypes from 'prop-types'
import { useField } from 'formik'
import classNames from 'classnames'

import { AlertText, FormLabel, HelperText } from 'library'
import styles from './styles.module.scss'

// We add 4ch to account for padding of input
const getInputWidth = (width) =>
  width ? { maxWidth: `${width + 4}ch` } : undefined

const TextInput = React.forwardRef(
  (
    {
      label,
      isInvalid,
      labelDescription,
      helperText,
      ariaDescribedBy,
      characterWidth,
      className,
      style,
      ...props
    },
    ref
  ) => {
    const { id, name } = props
    const idOrName = id || name
    const descriptionId = `${label}-desc-text`
    const helperTextId = `${label}-helper-text`

    let ariaDescriptionIds = []
    if (ariaDescribedBy) ariaDescriptionIds.push(ariaDescribedBy)
    if (labelDescription) ariaDescriptionIds.push(descriptionId)
    if (helperText) ariaDescriptionIds.push(helperTextId)
    ariaDescriptionIds = ariaDescriptionIds.join(' ')

    return (
      <div
        className={classNames(className, styles.textInputWrapper)}
        // If wrapped with FormRow, we extend component with characterWidth styles here
        style={style}
      >
        <FormLabel htmlFor={idOrName}>{label}</FormLabel>
        {labelDescription && (
          <HelperText id={descriptionId}>{labelDescription}</HelperText>
        )}
        <input
          id={idOrName}
          className={styles.textInput}
          aria-describedby={ariaDescriptionIds}
          {...props}
          aria-invalid={isInvalid}
          style={getInputWidth(characterWidth)}
          ref={ref}
        />
        {helperText && (
          <HelperText className={styles.helperText} id={helperTextId}>
            {helperText}
          </HelperText>
        )}
      </div>
    )
  }
)

TextInput.defaultProps = {
  characterWidth: null,
  isInvalid: null,
  labelDescription: null,
  helperText: null,
  ariaDescribedBy: null,
}

TextInput.displayName = 'TextInput'

TextInput.propTypes = {
  characterWidth: PropTypes.number,
  isInvalid: PropTypes.bool,
  label: PropTypes.string.isRequired,
  labelDescription: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]),
  helperText: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]),
  ariaDescribedBy: PropTypes.string,
}

const TextInputWrapper = ({ children, className, style, ...props }) => (
  <div
    className={classNames(className, styles.textInputContainer)}
    // If wrapped with FormRow, we extend component with characterWidth styles here
    style={style}
    {...props}
  >
    {children}
  </div>
)

TextInputWrapper.displayName = 'TextInputWrapper'

const FormTextInput = React.forwardRef(
  ({ label, style, className, ...props }, ref) => {
    // useField() returns [formik.getFieldProps(), formik.getFieldMeta()]
    // which we can spread on <input> and also replace ErrorMessage entirely.
    const [field, meta] = useField(props)
    const isErrorVisible = !!(meta.touched && meta.error)

    return (
      <TextInputWrapper style={style} className={classNames(className)}>
        <TextInput
          isInvalid={isErrorVisible}
          label={label}
          ariaDescribedBy={isErrorVisible ? `${label}-error` : undefined}
          ref={ref}
          {...field}
          {...props}
        />
        <AlertText
          isVisible={isErrorVisible}
          variant="danger"
          id={`${label}-error`}
        >
          {meta.error}
        </AlertText>
      </TextInputWrapper>
    )
  }
)

FormTextInput.displayName = 'FormTextInput'

export { TextInput, TextInputWrapper, FormTextInput }
